import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AlertDefinition, AlertTemplate, PersonalRuleInput, TelegramStatus } from '@qronstat/shared';
import { api } from './client';

export interface PersonalRule {
  id: string;
  name: string;
  template: string | null;
  definition: AlertDefinition;
  enabled: boolean;
  cooldownMinutes: number;
  notifyRecovery: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface TeamRuleSample {
  id: string;
  name: string;
  template: string | null;
  definition: AlertDefinition;
  cooldownMinutes: number;
  notifyRecovery: boolean;
}

export interface TelegramDelivery {
  id: string;
  kind: 'fire' | 'recover' | 'welcome' | 'test';
  title: string;
  body: string;
  createdAt: string;
  sentAt: string | null;
  error: string | null;
}

export interface TelegramLinkResponse {
  url: string;
  expiresAt: string;
  qrSvg: string;
  botUsername: string;
}

export const telegramStatusKey = ['telegram-status'] as const;
const rulesKey = ['notification-rules'] as const;
const deliveriesKey = ['telegram-deliveries'] as const;

export function useTelegramStatus(options: { refetchInterval?: number | false } = {}) {
  return useQuery({
    queryKey: telegramStatusKey,
    queryFn: () => api<TelegramStatus>('/api/me/telegram'),
    staleTime: 30_000,
    refetchInterval: options.refetchInterval ?? false,
  });
}

export function useNotificationRules() {
  return useQuery({
    queryKey: rulesKey,
    queryFn: () => api<{ items: PersonalRule[] }>('/api/me/notifications/rules'),
    staleTime: 30_000,
  });
}

export function useNotificationTemplates(enabled = true) {
  return useQuery({
    queryKey: ['notification-templates'],
    queryFn: () =>
      api<{ templates: AlertTemplate[]; team: TeamRuleSample[] }>('/api/me/notifications/templates'),
    enabled,
    staleTime: 5 * 60_000,
  });
}

export function useTelegramDeliveries(enabled = true) {
  return useQuery({
    queryKey: deliveriesKey,
    queryFn: () => api<{ items: TelegramDelivery[] }>('/api/me/telegram/deliveries?limit=10'),
    enabled,
    staleTime: 30_000,
  });
}

export function useTelegramActions() {
  const client = useQueryClient();
  const refresh = async () => {
    await Promise.all([
      client.invalidateQueries({ queryKey: telegramStatusKey }),
      client.invalidateQueries({ queryKey: rulesKey }),
      client.invalidateQueries({ queryKey: deliveriesKey }),
    ]);
  };
  return {
    refresh,
    link: useMutation({
      mutationFn: () => api<TelegramLinkResponse>('/api/me/telegram/link', { method: 'POST' }),
    }),
    unlink: useMutation({
      mutationFn: () => api('/api/me/telegram', { method: 'DELETE' }),
      onSuccess: refresh,
    }),
    test: useMutation({
      mutationFn: () => api('/api/me/telegram/test', { method: 'POST' }),
      onSuccess: () => client.invalidateQueries({ queryKey: deliveriesKey }),
    }),
    prompt: useMutation({
      mutationFn: (body: { action: 'skip' | 'configure'; never: boolean }) =>
        api<TelegramStatus>('/api/me/telegram/prompt', { method: 'POST', body }),
      onSuccess: (status) => client.setQueryData(telegramStatusKey, status),
    }),
    create: useMutation({
      mutationFn: (items: PersonalRuleInput[]) =>
        api<{ items: PersonalRule[] }>('/api/me/notifications/rules', { method: 'POST', body: { items } }),
      onSuccess: refresh,
    }),
    update: useMutation({
      mutationFn: ({ id, body }: { id: string; body: PersonalRuleInput }) =>
        api<PersonalRule>(`/api/me/notifications/rules/${id}`, { method: 'PUT', body }),
      onSuccess: refresh,
    }),
    toggle: useMutation({
      mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
        api<PersonalRule>(`/api/me/notifications/rules/${id}`, { method: 'PATCH', body: { enabled } }),
      onSuccess: refresh,
    }),
    remove: useMutation({
      mutationFn: (id: string) => api(`/api/me/notifications/rules/${id}`, { method: 'DELETE' }),
      onSuccess: refresh,
    }),
  };
}
